import { useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { effectiveCompanyRole, type CompanyPermission, type EmployeeRole } from 'shared'

/**
 * The current actor's company-management role + permission checks. `can` simply tests membership in
 * `user.permissions` — the FULL set the SERVER resolved (role grants ∪ static `orgs:*` grant; see
 * resolveCompanyPermissions in shared) and shipped on getUser — so the client never recomputes a role
 * table and the backend (`companyProcedure`) enforces the exact same set. Gate company-management UI with
 * this so it never offers an action the backend would reject (an `authorized` employee sees read-only
 * chrome, a `collaborator` sees no company management at all). The backend stays the source of truth —
 * this is UX only; clicking a hidden-but-reachable action still gets an INSUFFICIENT_PERMISSIONS rejection.
 *
 * `role` is still derived here (not a permission, but needed for interface gating below): a company
 * account is always 'administrator'; an employee carries the ACTIVE employer's membership role; anyone
 * else (self-registered professional, or an employee on their personal D'Anchiano profile) has none.
 */
export function usePermissions(): {
  role: EmployeeRole | null
  isCompanyContext: boolean
  can: (permission: CompanyPermission) => boolean
  // Block C: render the COMPANY interface (a company account, or a promoted employee viewing 'company').
  isCompanyView: boolean
  // The current promoted-employee view (server-resolved + role-gated); 'employee' for everyone else.
  interfaceMode: 'employee' | 'company'
  // A promoted admin/authorized employee who can toggle between their employee profile and the company view.
  canToggleInterface: boolean
} {
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())

  const role: EmployeeRole | null = user ? effectiveCompanyRole(user) : null

  const isEmployee = user?.type === 'applicant' && user.isEmployee
  const interfaceMode = user?.interfaceMode ?? 'employee'
  // A promoted admin/authorized employee (NOT the principal admin's self-employee — that one switches
  // identity via adminOfCompanyId, handled separately) may switch between the two views.
  const canToggleInterface =
    isEmployee && user?.activeCompanyId != null && (role === 'administrator' || role === 'authorized')

  return {
    role,
    // Operating inside a company context: a company account, or an employee acting under an employer.
    isCompanyContext: role != null,
    can: (permission: CompanyPermission) => user?.permissions?.includes(permission) ?? false,
    isCompanyView: user?.type === 'company' || (isEmployee && interfaceMode === 'company'),
    interfaceMode,
    canToggleInterface,
  }
}
